// Data Analytics Course Data
import { FaDatabase } from 'react-icons/fa';
import { FaChartColumn } from 'react-icons/fa6';
import { FaSquarePollVertical } from 'react-icons/fa6';
import { FaMicrosoft } from 'react-icons/fa6';
import { FaRegFileExcel } from 'react-icons/fa6';
import { FaPython } from 'react-icons/fa';
import { FaAward } from 'react-icons/fa6';
import { FaGraduationCap } from 'react-icons/fa6';
import { FaUserTie } from 'react-icons/fa6';
import { FaExchangeAlt } from 'react-icons/fa';
import SQLImg from '../assets/tools/SQL.svg';
import ExcelImg from '../assets/tools/Excel.png';
import PowerBIImg from '../assets/tools/PowerBI.svg';
import PythonImg from '../assets/data-science/Python.png';
import PandasImg from '../assets/data-science/Pandas.png';
import ScikitImg from '../assets/data-science/Scikit.png';

const dataAnalyticsCourse = {
  id: 'data-analytics',
  title: 'Data Analytics Training & Internship Program',
  shortTitle: 'Data Analytics',
  hero: {
    headline: 'Data Analytics Training and Internship - Certificate Program',
    subheadline: 'UK Industry-Microsoft Partnered Certified Program | Dashboards & Project Portfolio | Live Interactive Mentorship',
    description: 'Join Int-Elligence Tech Solutions Plc and gain practical analytics skills employers in the UK demand: SQL mastery, business-grade Excel, Power BI dashboarding, and Python-backed analysis — all delivered with live mentor guidance and portfolio projects that get you noticed.',
    statistics: [
      { value: '90%', label: 'of UK employers value data-driven decision making', ref: 'Tech Nation 2024' },
      { value: '£30K-£55K', label: 'starting salaries for analytics roles', ref: 'Economic Research Institute' },
      { value: '+150%', label: 'growth in demand for analytics specialists over five years', ref: 'Royal Society' },
      { value: 'Portfolio-first', label: 'Real projects that demonstrate capability to employers', ref: 'Program focus' }
    ]
  },
  importantNotice: {
    title: 'IMPORTANT NOTICE',
    requirements: [
      'Treat this as a career accelerator, not just a certificate.',
      'Be ready to complete weekly assignments and project tasks.',
      'Actively engage in mentor feedback and review sessions.'
    ],
    reason: 'Our mentors invest significant time in each participant, so we prioritize applicants who are serious about becoming analytics professionals.'
  },
  programHighlights: {
    duration: '18 Weeks',
    features: [
      {
        icon: <FaDatabase />,
        title: '1. SQL Foundations',
        description: 'Learn to query, filter, join, aggregate, and analyze data effectively.'
      },
      {
        icon: <FaRegFileExcel />,
        title: '2. Excel for Business Analysis',
        description: 'Use spreadsheets for reporting, cleaning, modeling, and decision support.'
      },
      {
        icon: <FaChartColumn />,
        title: '3. Power BI Dashboards',
        description: 'Create interactive dashboards and communicate insights with impact.'
      },
      {
        icon: <FaPython />,
        title: '4. Python and ML for Analytics',
        description: 'Apply Python, pandas, and machine learning basics to solve analytics problems.'
      },
      {
        icon: <FaSquarePollVertical />,
        title: '5. Real-World Projects',
        description: 'Build portfolio work that demonstrates practical analysis and reporting skills.'
      },
      {
        icon: <FaAward />,
        title: '6. Microsoft-Partnered Certification',
        description: 'Strengthen your professional profile with recognized certification.'
      }
    ]
  },
  toolsCovered: [
    { name: 'SQL', description: 'Query databases, shape datasets, and answer business questions.', image: SQLImg },
    { name: 'Excel', description: 'Clean, analyze, and present data using formulas and pivot tables.', image: ExcelImg },
    { name: 'Power BI', description: 'Create dashboards, reports, and interactive business visuals.', image: PowerBIImg },
    { name: 'Python', description: 'Automate analysis and build data workflows for repeatable work.', image: PythonImg },
    { name: 'Pandas', description: 'Manipulate, transform, and explore data efficiently.', image: PandasImg },
    { name: 'Scikit-learn', description: 'Use simple machine learning models for prediction and classification.', image: ScikitImg }
  ],
  whyThisProgram: [
    {
      title: 'Role-Relevant Skills',
      description: 'Focus on the analytics tools employers actually ask for in interviews.'
    },
    {
      title: 'Business-Focused Projects',
      description: 'Practice with reports, dashboards, and analyses that mirror real teams.'
    },
    {
      title: 'Mentor Feedback',
      description: 'Get support on SQL logic, dashboard design, and analysis storytelling.'
    },
    {
      title: 'Career-Ready Portfolio',
      description: 'Leave with work samples you can show to recruiters and hiring managers.'
    },
    {
      title: 'Flexible Learning',
      description: 'Follow a structured path while studying around work or university.'
    },
    {
      title: 'UK Industry Alignment',
      description: 'Train with a program shaped around UK hiring expectations and standards.'
    }
  ],
  programDetails: {
    overview: {
      title: 'Program Overview and Duration',
      description: 'Our Data Analytics Training and Internship - Certificate Program is designed to move you from fundamentals to applied project work in 18 weeks.',
      phases: [
        {
          title: 'Weeks 1-3: SQL',
          description: 'Build a strong foundation in querying databases, joins, aggregations, and analytic thinking.'
        },
        {
          title: 'Weeks 4-7: Excel',
          description: 'Use Excel for data cleaning, reporting, formulas, dashboards, and business analysis tasks.'
        },
        {
          title: 'Weeks 8-10: Power BI',
          description: 'Create clear dashboards and visual reports that communicate insights to stakeholders.'
        },
        {
          title: 'Weeks 11-14: Python / ML',
          description: 'Apply Python, pandas, and introductory machine learning methods to analytics problems.'
        },
        {
          title: 'Weeks 15-18: Real-World Projects',
          description: 'Work on end-to-end portfolio projects that combine SQL, BI, and Python analysis.'
        }
      ]
    },
    modeOfDelivery: {
      title: 'Mode of Delivery',
      description: 'The program is fully remote and combines self-paced learning, mentor check-ins, and practical assignments. It is a learning-focused unpaid program built around real-world outcomes.'
    },
    commitment: {
      title: 'Commitment and Flexibility',
      description: 'Participants should plan for at least 8 hours per week to keep up with lessons, assignments, and project deliverables.'
    },
    curriculum: {
      title: 'Curriculum and Hands-On Experience',
      description: 'You will progress through a practical curriculum that blends analysis, reporting, and applied problem solving.',
      points: [
        'Learn SQL, Excel, Power BI, and Python through guided lessons and tasks.',
        'Practice cleaning, transforming, and interpreting datasets for business use cases.',
        'Deliver project work that demonstrates your ability to communicate insights clearly.'
      ]
    },
    mentorship: {
      title: 'Mentorship',
      description: 'You will be paired with a mentor for weekly live sessions that focus on technical questions, project feedback, and career guidance.'
    },
    certification: {
      title: 'Certification and Credentials',
      description: 'Successful participants receive a Microsoft-Partnered Certificate issued by Int-Elligence Tech Solutions Plc.'
    },
    careerAdvancement: {
      title: 'Career Advancement',
      description: 'Strong performers may be considered for additional opportunities after the program, subject to interview and business needs.',
      additionalInfo: 'This program is designed to help learners move into analytics-focused roles with practical evidence of their work.',
      careerPaths: 'Typical roles include Data Analyst, Reporting Analyst, Business Intelligence Analyst, and Junior Analytics Consultant.'
    }
  },
  whoShouldApply: [
    {
      icon: <FaGraduationCap />,
      title: 'Graduates',
      description: 'Build employable analytics skills and a strong project portfolio.'
    },
    {
      icon: <FaUserTie />,
      title: 'Professionals',
      description: 'Upskill into analytics, reporting, or BI-focused roles.'
    },
    {
      icon: <FaExchangeAlt />,
      title: 'Career Switchers',
      description: 'Transition into data roles with structured, mentor-led learning.'
    }
  ],
  aluminiVideos: [
    {
      id: 1,
      name: 'Yemisi Ohiorenoya',
      videoUrl: 'https://pub-1a559929ef4149c4abff31d55531033a.r2.dev/yemisi%20(2).mp4'
    },
    {
      id: 2,
      name: 'Afifa Anis',
      videoUrl: 'https://pub-1a559929ef4149c4abff31d55531033a.r2.dev/afifa.mp4'
    },
    {
      id: 3,
      name: 'Obanla Oluwaseun',
      videoUrl: 'https://pub-1a559929ef4149c4abff31d55531033a.r2.dev/obanla.mp4'
    }
  ],
  whyAlumniGetHired: [
    {
      icon: '✅',
      title: 'Strong SQL Skills',
      description: 'Graduates can query, aggregate, and analyze data confidently.'
    },
    {
      icon: '✅',
      title: 'Dashboard Portfolio',
      description: 'Completed Power BI and Excel work makes impact easy to demonstrate.'
    },
    {
      icon: '✅',
      title: 'Applied Python',
      description: 'Participants can show basic automation and ML-informed analysis.'
    }
  ],
  testimonialLink: 'https://share.google/wv45vd8NA35pedVmw',
  contactEmail: 'hr@int-elligence.co.uk',
  footerNote: 'The UK needs more analytics professionals who can turn data into decisions.'
};

export default dataAnalyticsCourse;